import { PocTypography } from 'commons/components/components-wrapper/poc-typography.component';
import { usePocMutation } from 'core/custom-hooks/use-poc-mutation.hook';
import { usePocQuery } from 'core/custom-hooks/use-poc-query.hook';
import { useUserPreferencesContext } from 'core/contexts/user-preferences/hooks/use-user-preferences-context.hook';
import { useTranslation } from 'core/i18n/i18n';
import { settingsKeys } from 'core/query-client/keys/settings.keys';
import { updateQuery } from 'core/query-client/query-client';
import { executeRequest } from 'core/utils/backend-service';
import { showErrorNotification, showSuccessNotification } from 'core/utils/notification-service';
import { SettingsViewContent, SettingsViewHeader, SettingsViewLayout } from 'modules/settings/settings.style';
import { useEffect, useState } from 'react';
import {
    getTrainingProgramsSettings,
    updateTrainingProgramsSettings,
} from './service/training-program-settings.service';
import { TrainingProgramsApplicableLocationsConfigComponent } from './applicable-locations/training-programs-applicable-locations-config.component';
import { ApplicableLocationsConfig, TrainerAppConfig, TrainingProgramsConfig } from './domain';
import { DEFAULT_TRAINING_PROGRAMS_CONFIG } from './domain/model/constants';
import { SettingsTrainingBody } from './settings-training-settings.style';
import { TrainingProgramsTrainerAppConfigComponent } from './trainer-app/training-programs-trainer-app-config.component';

export const SettingsTrainingSettingsContainer = () => {
    const { t } = useTranslation();

    const { setAppSettings } = useUserPreferencesContext();

    const [trainingProgramSettings, setTrainingProgramSettings] = useState<TrainingProgramsConfig>(
        DEFAULT_TRAINING_PROGRAMS_CONFIG
    );

    const { data: loadedSettings, isFetching } = usePocQuery<TrainingProgramsConfig>({
        queryKey: settingsKeys.trainingSettings(),
        queryFn: () => executeRequest(() => getTrainingProgramsSettings()),
    });

    // Seed the locally-mutable settings from the query (updated optimistically on each save below).
    useEffect(() => {
        if (loadedSettings) {
            setTrainingProgramSettings(loadedSettings);
        }
    }, [loadedSettings]);

    const { mutateAsync: updateSettings, isPending: isSaving } = usePocMutation({
        mutationFn: (settings: TrainingProgramsConfig) =>
            executeRequest(() => updateTrainingProgramsSettings(settings)),
        // Keep the 5-min-cached settings query in sync so a later remount doesn't reseed stale values.
        onSuccess: () => updateQuery(settingsKeys.trainingSettings()),
    });

    const isTrainingProgramsConfigLoading = isFetching || isSaving;

    const handleOnApplicableLocationsConfigChange = (config: ApplicableLocationsConfig) => {
        const newTrainingProgramSettings = { ...trainingProgramSettings, applicableLocationsConfig: config };
        updateSettings(newTrainingProgramSettings)
            .then(() => {
                setTrainingProgramSettings(newTrainingProgramSettings);
                showSuccessNotification(t('settings.training-programs.applicable-locations.notification.success'));
                setAppSettings((prev) => ({
                    ...prev,
                    trainingProgramsConfig: {
                        ...prev.trainingProgramsConfig,
                        applicableLocationsConfig: config,
                    },
                }));
            })
            .catch(() => {
                showErrorNotification(t('settings.training-programs.applicable-locations.notification.error'));
            });
    };

    const handleOnTrainerAppConfigChange = (config: TrainerAppConfig) => {
        const newTrainingProgramSettings = { ...trainingProgramSettings, trainerAppConfig: config };
        updateSettings(newTrainingProgramSettings)
            .then(() => {
                setTrainingProgramSettings(newTrainingProgramSettings);
                showSuccessNotification(t('settings.training-programs.trainer-app.notification.success'));
                setAppSettings((prev) => ({
                    ...prev,
                    trainingProgramsConfig: {
                        ...prev.trainingProgramsConfig,
                        trainerAppConfig: config,
                    },
                }));
            })
            .catch(() => {
                showErrorNotification(t('settings.training-programs.trainer-app.notification.error'));
            });
    };

    return (
        <SettingsViewLayout>
            <SettingsViewHeader>
                <PocTypography element="h5" variant="title5">
                    {t('settings.training-programs.title')}
                </PocTypography>
                <SettingsTrainingBody>{t('settings.training-programs.subtitle')}</SettingsTrainingBody>
            </SettingsViewHeader>
            <SettingsViewContent>
                <TrainingProgramsTrainerAppConfigComponent
                    config={trainingProgramSettings.trainerAppConfig}
                    onConfigChange={handleOnTrainerAppConfigChange}
                    loading={isTrainingProgramsConfigLoading}
                />
                <TrainingProgramsApplicableLocationsConfigComponent
                    config={trainingProgramSettings.applicableLocationsConfig}
                    onConfigChange={handleOnApplicableLocationsConfigChange}
                    loading={isTrainingProgramsConfigLoading}
                />
            </SettingsViewContent>
        </SettingsViewLayout>
    );
};
